/**
 * One display name for a chat row, used everywhere a channel is listed.
 *
 * `chats.chat_id` is nullable (a row scraped before Telegram resolved the id),
 * so the old `chat.chat_name || `Chat ${chat.chat_id}`` printed the literal
 * string "Chat null". The fallbacks below are translated and always identify
 * the row: by its Telegram id when there is one, by the internal row id when
 * there is not.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Chat } from '@/types/api';

export type ChatLabelSource = Pick<Chat, 'id' | 'chat_name' | 'chat_id'>;

/**
 * FSI…PDI around the number. Without it a Latin-digit id at the end of an
 * Arabic sentence is reordered by the bidi algorithm and reads wrong.
 */
const isolateId = (value: number): string => `⁨#${value}⁩`;

export const useChatLabel = () => {
  const { t } = useTranslation('common');

  return useCallback(
    (chat: ChatLabelSource): string => {
      const name = chat.chat_name?.trim();
      if (name) return name;
      if (chat.chat_id !== null && chat.chat_id !== undefined) {
        return t('chatFallback.withId', { id: isolateId(chat.chat_id) });
      }
      return t('chatFallback.noTelegramId', { id: isolateId(chat.id) });
    },
    [t]
  );
};
